import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { I2gDialogService, IconSizesEnum, IIcon } from 'i2g-ng-ui';
import { v4 } from 'uuid';
import { TranslateService } from '@ngx-translate/core';
import { AttachmentErrorDialogComponent } from '../../../shared/components/attachment-error-dialog/attachment-error-dialog.component';
import { AttachmentsService } from '../../../attachments/attachments.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { AttachmentsStore } from '../../../attachments/attachments.store';
import { fromJS, List } from 'immutable';
import { AttachmentView } from '../../../attachments/attachment-view.model';
import { Attachment } from '../../../attachments/attachment.model';
import { Document } from '../../../document-data/document.model';
import { IDocumentDataDispatcher } from '../../../document-data/document-data-dispatcher.interface';
import { MAX_EMAIL_SIZE } from '../../../document-data/document-data.data';
import {
  ITrackingObject,
  TrackingInputTypeValuesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import {
  I2gNgTrackingActionBuilder,
  ITrackingAction,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { map, takeUntil } from 'rxjs/operators';
import { FileSizeService } from '../../../shared/services/file-size/file-size.service';

@Component({
  selector: 'app-file-attachment',
  templateUrl: './file-attachment.component.html',
  styleUrls: ['./file-attachment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileAttachmentComponent implements OnInit, OnChanges {
  @Input() attachments: List<AttachmentView>; // from attachment store: used to save the uploading files from '/files'
  // endpoint
  @Input() fileAttachments: List<Attachment>; // from document schema and saved in the server
  @Input() attachmentName: string;
  @Input() attachmentSize: number;
  @Input() documentDataDispatcher: IDocumentDataDispatcher;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() trackingObject: ITrackingObject;
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  attachmentSelectId: string;
  acceptableFileTypes: string[];
  errorMessage: string;
  attachmentBlueIcon: IIcon;
  attachmentClipIcon: IIcon;
  attachmentLoadingIcon: IIcon;
  loadingIcon: IIcon;
  closeIcon: IIcon;
  totalAttachmentNumber = 9; // combined attachments should be less than 10 in total
  readableAttachmentSize: string;
  combinedFileAttachments$: BehaviorSubject<List<any>> = new BehaviorSubject<
    List<any>
  >(null);
  originalAttachments: List<Attachment>;
  addAttachmentAction: ITrackingAction;
  deleteAttachmentAction: ITrackingAction;
  fileTypeTrackingAdditionalProperties: ITrackingProperties;
  combinedSizes: number;
  initialized = false;
  showUploadingIcon: boolean;
  formattedAttachmentName: string;

  constructor(
    private _translateService: TranslateService,
    private _dialogService: I2gDialogService,
    private _attachmentsService: AttachmentsService,
    private _attachmentsStore: AttachmentsStore,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingDispatcher: TrackingDispatcher,
    private _fileSizeService: FileSizeService
  ) {}

  ngOnInit() {
    this.attachmentSelectId = v4();
    this.acceptableFileTypes = ['application/pdf'];
    this.formattedAttachmentName = this.attachmentName.split('.pdf')[0];
    this.attachmentBlueIcon = {
      name: 'attachment-blue',
      size: IconSizesEnum.custom,
    };
    this.attachmentClipIcon = {
      name: 'add-attachment',
      size: IconSizesEnum.medium,
    };
    this.attachmentLoadingIcon = {
      name: 'attachment-loading',
      size: IconSizesEnum.small,
    };
    this.closeIcon = {
      name: 'close-navigation',
      size: IconSizesEnum.custom,
    };
    this.loadingIcon = {
      name: 'attachment-loading',
      size: IconSizesEnum.large,
    };

    // filter uploading progress which is 0% and 100% only to decide whether show loading icon or not
    this._attachmentsStore.inProgress$
      .pipe(
        map((attachments) => {
          return attachments
            .toArray()
            .filter(
              (attachment) =>
                attachment.progress === 0 || attachment.progress === 100
            )
            .map((attachment) => attachment.progress);
        }),
        takeUntil(this._destroyed)
      )
      .subscribe((inProgressArray) => {
        inProgressArray.map((value) => {
          this.showUploadingIcon = value < 100;
        });
      });

    if (this.fileAttachments) {
      this.combinedFileAttachments$.next(
        this.getFormattedFileAttachment(this.fileAttachments)
      );
    }
    this.originalAttachments = this.fileAttachments;

    this.addAttachmentAction = this._trackingActionBuilder.buildButtonTapped(
      'add_attachments',
      this.trackingScreenName
    );
    this.deleteAttachmentAction = this._trackingActionBuilder.buildButtonTapped(
      'delete_attachment',
      this.trackingScreenName
    );
    this.fileTypeTrackingAdditionalProperties = {
      file_type: 'pdf',
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.attachmentSize) {
      this.readableAttachmentSize = this._fileSizeService.getReadableSize(
        this.attachmentSize,
        1
      );
    }

    // If attachments from document schema or attachment stores changes, trigger the update of whe whole file
    // attachment list
    if (
      (changes.attachments && !changes.attachments.isFirstChange()) ||
      (changes.fileAttachments && this.initialized)
    ) {
      this.initialized = true;
      const attachmentChanges = this.attachments.map(
        (attachment) => attachment.attachment
      ) as List<Attachment>;
      const concatChanges = this.originalAttachments.concat(
        attachmentChanges
      ) as List<Attachment>;
      this.changeAttachmentList(concatChanges);
    }

    // add tracking when user add/delete files
    if (changes.attachments && !changes.attachments.isFirstChange()) {
      this.trackNewAttachmentsChanges(
        changes.attachments.previousValue,
        this.attachments
      );
    }
  }

  changeAttachmentList(attachmentChanges: List<Attachment>) {
    const documentContentPropsFromAttachmentList = fromJS({
      [Document.CONTENT_KEYS.FILE_ATTACHMENTS]: attachmentChanges,
    });
    this.combinedFileAttachments$.next(
      this.getFormattedFileAttachment(attachmentChanges)
    );
    this.documentDataDispatcher.setEditingEntityContent(
      documentContentPropsFromAttachmentList
    );
  }

  onAddFiles(evt: any) {
    const inputElement = evt.srcElement || evt.target;
    const files = inputElement.files;
    const reducer = (accumulator, currentValue) => accumulator + currentValue;

    // calculate the total size of selected files
    const filesArray = Object.keys(files).map((key) => files[key]);
    const allSelectedFileSize = filesArray.map((file) => file.size);
    const totalSelectedFileSizes = allSelectedFileSize.reduce(reducer);

    // calculate the total size of attached files + document pdf file
    const allAttachedFileSizes = this.fileAttachments.map(
      (attachment) => attachment.file.fileSize
    );
    const totalPdfFileSizes =
      allAttachedFileSizes.size === 0
        ? this.attachmentSize
        : allAttachedFileSizes.reduce(reducer) + this.attachmentSize;

    if (totalSelectedFileSizes > MAX_EMAIL_SIZE - totalPdfFileSizes) {
      this._dialogService.openDialog(AttachmentErrorDialogComponent, {
        isDismissible: true,
        attachFileErrorDescription: this._translateService.instant(
          'FILE_ATTACHMENT.CAN_NOT_ATTACH_FILE_TOO_LARGE_DESCRIPTION'
        ),
        errorMessageTrackingAdditionalProperties: {
          error_message: 'attachment_size_limit_exceeded',
        },
      });
      inputElement.value = null;
      return;
    }

    for (const file of files) {
      if (!this.acceptableFileTypes.includes(file.type)) {
        this._dialogService.openDialog(AttachmentErrorDialogComponent, {
          isDismissible: true,
          attachFileErrorDescription: this._translateService.instant(
            'ERROR.UNSUPPORTED_FILE_TYPE'
          ),
        });
        inputElement.value = null;
        return;
      }
    }

    if (this.attachments.size < this.totalAttachmentNumber) {
      if (
        files.length >
        this.totalAttachmentNumber - this.fileAttachments.size
      ) {
        this._dialogService.openDialog(AttachmentErrorDialogComponent, {
          isDismissible: true,
          attachFileErrorDescription: this._translateService.instant(
            'FILE_ATTACHMENT.CAN_NOT_ATTACH_FILE_TOO_MANY_DESCRIPTION',
            {
              maxNumber: this.totalAttachmentNumber + 1,
            }
          ),
          errorMessageTrackingAdditionalProperties: {
            error_message: 'attachment_count_limit_exceeded',
          },
        });
      } else {
        this._attachmentsService.addToQueue(files);
      }
    }
  }

  onDeleteAttachment(attachmentId: string) {
    const attachmentDeleted = this._trackingActionBuilder.buildInputUnassigned(
      attachmentId,
      null,
      TrackingInputTypeValuesEnum.file_attachment
    );

    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      attachmentDeleted,
      this.fileTypeTrackingAdditionalProperties
    );

    this.originalAttachments = this.originalAttachments.filter(
      (originalAttachment: Attachment) => originalAttachment.id !== attachmentId
    ) as List<Attachment>;

    const attachmentChanges = this.fileAttachments.filter(
      (fileAttachment: Attachment) => fileAttachment.id !== attachmentId
    ) as List<Attachment>;
    this.changeAttachmentList(attachmentChanges);
    this._attachmentsStore.deleteAttachmentById(attachmentId);
  }

  getFormattedFileAttachment(fileAttachments: List<Attachment>) {
    return fileAttachments
      .map((attachment) => ({
        id: attachment.id,
        title: attachment.title,
        size: this._fileSizeService.getReadableSize(
          attachment.file.fileSize,
          1
        ),
      }))
      .toList();
  }

  trackNewAttachmentsChanges(
    previousList: List<AttachmentView>,
    currentList: List<AttachmentView>
  ) {
    currentList
      .filter((attachment) => attachment.emitEvent)
      .toSet()
      .subtract(previousList)
      .map((attachment) =>
        this._trackingActionBuilder.buildInputAssigned(
          attachment.id,
          null,
          TrackingInputTypeValuesEnum.file_attachment
        )
      )
      .forEach((trackingAction) =>
        this._trackingDispatcher.trackEvent(
          this.trackingObject,
          trackingAction,
          this.fileTypeTrackingAdditionalProperties
        )
      );
  }
}
